import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Camera, ImagePlus, Sparkles, Upload, Loader2, X, CheckCircle2, RotateCcw, ArrowLeft } from 'lucide-react';

// Downscale a camera/gallery photo in the browser (max 1600px, JPEG) so the request stays small.
const shrink = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const max = 1600; const s = Math.min(1, max / Math.max(img.width, img.height));
    const c = document.createElement('canvas'); c.width = Math.round(img.width * s); c.height = Math.round(img.height * s);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(url); resolve(c.toDataURL('image/jpeg', 0.88));
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read photo')); };
  img.src = url;
});

const usd = (v) => `~$${(v || 0).toFixed(2)} (≈₹${Math.round((v || 0) * 85)})`;

// Steps: pick → choose (upload as-is / AI edit) → preview → saved. Image save is optional and never touches audit approvals.
export const ProductImagePanel = ({ line, api, onSaved, onClose }) => {
  const [step, setStep] = useState('pick');
  const [source, setSource] = useState('');
  const [opts, setOpts] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const camRef = useRef(null); const galRef = useRef(null);

  useEffect(() => { api.options().then(setOpts).catch(() => setOpts({ storage_ready: false, ai_ready: false })); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    setErr('');
    try { setSource(await shrink(f)); setStep('choose'); } catch (ex) { setErr(ex.message); }
  };
  const run = async (mode) => {
    setBusy(mode); setErr('');
    try { setPreview(await api.preview({ product_id: line.product_id, image: source, mode })); setStep('preview'); }
    catch (ex) { setErr(ex); }
    finally { setBusy(''); }
  };
  const save = async () => {
    setBusy('save'); setErr('');
    try { const r = await api.confirm({ draft_id: preview.draft_id }); setStep('saved'); onSaved(r); }
    catch (ex) { setErr(ex); }
    finally { setBusy(''); }
  };

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-3 space-y-3" data-testid="product-image-panel">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-indigo-900 flex items-center gap-1"><ImagePlus className="w-4 h-4" /> Product photo <span className="text-[10px] font-normal text-indigo-600">(optional · updates product master & website instantly)</span></div>
        <button onClick={onClose} className="text-gray-400" aria-label="Close photo panel" data-testid="product-image-close"><X className="w-4 h-4" /></button>
      </div>
      {opts && !opts.storage_ready && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="product-image-no-storage">Image storage (Bunny CDN) is not configured — ask HQ to set it in Settings → Assets.</div>}
      {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="product-image-error">{err}</div>}

      {step === 'pick' && (
        <div className="grid grid-cols-2 gap-2">
          <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="product-image-camera-input" />
          <input ref={galRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="product-image-gallery-input" />
          <Button variant="outline" className="h-14 bg-white flex-col gap-0.5" onClick={() => camRef.current?.click()} disabled={opts && !opts.storage_ready} data-testid="product-image-take-photo"><Camera className="w-5 h-5" /><span className="text-xs">Take photo</span></Button>
          <Button variant="outline" className="h-14 bg-white flex-col gap-0.5" onClick={() => galRef.current?.click()} disabled={opts && !opts.storage_ready} data-testid="product-image-pick-gallery"><Upload className="w-5 h-5" /><span className="text-xs">From gallery</span></Button>
        </div>
      )}

      {step === 'choose' && (
        <div className="space-y-2">
          <img src={source} alt="" className="w-full max-h-56 object-contain rounded-lg bg-white border" data-testid="product-image-source" />
          <div className="text-xs text-gray-600">How should this photo be saved?</div>
          <Button className="w-full h-auto py-2 bg-indigo-600 hover:bg-indigo-700 justify-start whitespace-normal text-left" onClick={() => run('ai')} disabled={!!busy || !opts?.ai_ready} data-testid="product-image-ai-edit">
            {busy === 'ai' ? <Loader2 className="w-4 h-4 mr-2 animate-spin shrink-0" /> : <Sparkles className="w-4 h-4 mr-2 shrink-0" />}
            <span className="leading-tight min-w-0"><div>AI edit — clean white studio background</div><div className="text-[10px] font-normal opacity-80">1024×1024 · {opts ? usd(opts.ai_cost_usd) : ''} per edit · review before saving</div></span>
          </Button>
          <Button variant="outline" className="w-full h-auto py-2 bg-white justify-start whitespace-normal text-left" onClick={() => run('upload')} disabled={!!busy} data-testid="product-image-upload-plain">
            {busy === 'upload' ? <Loader2 className="w-4 h-4 mr-2 animate-spin shrink-0" /> : <Upload className="w-4 h-4 mr-2 shrink-0" />}
            <span className="leading-tight min-w-0"><div>Upload as-is (no AI)</div><div className="text-[10px] font-normal text-gray-500">Only resized to 1024×1024 WebP · free</div></span>
          </Button>
          {busy === 'ai' && <div className="text-[11px] text-indigo-700 text-center">AI is editing the background — this takes 10–30 seconds…</div>}
          <button className="text-xs text-gray-500 underline" onClick={() => { setStep('pick'); setSource(''); }} data-testid="product-image-back"><ArrowLeft className="w-3 h-3 inline" /> choose another photo</button>
        </div>
      )}

      {step === 'preview' && preview && (
        <div className="space-y-2">
          <div className="text-xs text-gray-700 font-medium">Review before saving {preview.mode === 'ai' ? '(AI edited)' : '(optimised)'} · {Math.round(preview.size / 1024)} KB WebP</div>
          <div className="grid grid-cols-2 gap-2">
            <div><div className="text-[10px] text-gray-500 mb-0.5">Original</div><img src={source} alt="" className="w-full aspect-square object-contain rounded-lg bg-white border" /></div>
            <div><div className="text-[10px] text-indigo-700 mb-0.5">Will be saved</div><img src={preview.preview} alt="" className="w-full aspect-square object-contain rounded-lg bg-white border-2 border-indigo-400" data-testid="product-image-preview" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="bg-white" onClick={() => setStep('choose')} disabled={!!busy} data-testid="product-image-retry"><RotateCcw className="w-4 h-4 mr-1" /> Redo</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={save} disabled={!!busy} data-testid="product-image-save">{busy === 'save' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />} Save to product</Button>
          </div>
        </div>
      )}

      {step === 'saved' && <div className="text-sm text-emerald-700 flex items-center gap-1" data-testid="product-image-saved"><CheckCircle2 className="w-4 h-4" /> Photo saved — product master and website updated.</div>}
    </div>
  );
};
